import { useState } from 'react';
import { Button, CircularProgress, Box } from '@mui/material';
import { CognitoService } from '../../services/api/auth/cognito.service';
import { logger } from '../../utils/logger';

interface GoogleSignInButtonProps {
  mode: 'signin' | 'signup';
  disabled?: boolean;
  onBeforeRedirect?: () => Promise<void> | void;
  fullWidth?: boolean;
}

// Google "G" logo as inline SVG
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <g fill="none" fillRule="evenodd">
      <path
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </g>
  </svg>
);

const GoogleSignInButton = ({
  mode,
  disabled = false,
  onBeforeRedirect,
  fullWidth = true,
}: GoogleSignInButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async () => {
    if (isLoading || disabled) return;

    setIsLoading(true);
    try {
      // Execute any pre-redirect logic (e.g., storing signup data)
      if (onBeforeRedirect) {
        await onBeforeRedirect();
      }

      // Initiate Google Sign-In
      await CognitoService.signInWithGoogle();
    } catch (error) {
      logger.error('Google sign-in failed:', error);
      setIsLoading(false);
      // The error will be caught by the component that renders this button
    }
  };

  const buttonText = mode === 'signin' ? 'Sign in with Google' : 'Sign up with Google';

  return (
    <Button
      variant="outlined"
      fullWidth={fullWidth}
      disabled={disabled || isLoading}
      onClick={handleClick}
      startIcon={
        isLoading ? <CircularProgress size={18} sx={{ color: 'rgba(255,255,255,0.6)' }} /> : null
      }
      sx={{
        py: 1.5,
        borderRadius: '12px',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        color: 'white',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        textTransform: 'none',
        fontSize: '0.95rem',
        fontWeight: 500,
        '&:hover': {
          borderColor: 'rgba(255, 255, 255, 0.4)',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
        '&:disabled': {
          borderColor: 'rgba(255, 255, 255, 0.1)',
          color: 'rgba(255, 255, 255, 0.4)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {!isLoading && <GoogleIcon />}
        {isLoading ? 'Redirecting...' : buttonText}
      </Box>
    </Button>
  );
};

export default GoogleSignInButton;
